import { useEffect, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Music4, Play, Pause, Download, Printer, SkipBack, SkipForward, Volume2, VolumeX, Link as LinkIcon, ExternalLink, GripVertical, ArrowUp, ArrowDown, Users2, User } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSiteLogo } from "@/lib/siteConfig";

async function urlToImage(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new window.Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  const fmt = dataUrl.substring(dataUrl.indexOf("/") + 1, dataUrl.indexOf(";")).toUpperCase();
  return { dataUrl, width: img.naturalWidth, height: img.naturalHeight, fmt: fmt === "JPG" ? "JPEG" : fmt };
}

const discLabel = { artistica: "Artistica", ritmica: "Ritmica" };
const TYPE_LABEL = { coppia: "Coppia", squadra: "Squadra", combinata: "Combinata" };
const GROUP_TYPES = ["coppia", "squadra", "combinata"];

const isDirectAudio = (url) => /\.(mp3|wav|ogg|m4a|aac|flac)(\?.*)?$/i.test(url || "");

function getEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    let ytId = null;
    if (host.includes("youtube.com")) ytId = u.searchParams.get("v");
    else if (host === "youtu.be") ytId = u.pathname.slice(1);
    if (ytId) return { type: "YouTube", src: `https://www.youtube.com/embed/${ytId}?autoplay=1`, height: 315 };
    if (host.includes("music.apple.com")) {
      return { type: "Apple Music", src: url.replace("music.apple.com", "embed.music.apple.com"), height: 175 };
    }
    if (host.includes("spotify.com")) {
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => ["track", "album", "playlist", "episode", "show"].includes(p));
      if (idx >= 0 && parts[idx + 1]) return { type: "Spotify", src: `https://open.spotify.com/embed/${parts[idx]}/${parts[idx + 1]}`, height: 152 };
    }
    if (host.includes("soundcloud.com")) return { type: "SoundCloud", src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=true`, height: 152 };
    return null;
  } catch {
    return null;
  }
}

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function ProgramSection() {
  const { user } = useAuth();
  const canExport = ["admin", "segreteria"].includes(user?.role);
  const logoUrl = useSiteLogo();
  const [events, setEvents] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dragCtx, setDragCtx] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [eventId, setEventId] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const audioRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [extTrack, setExtTrack] = useState(null);
  const levelOptions = [...new Set(athletes.filter((a) => a.level).map((a) => a.level))].sort();

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);
  useEffect(() => { api.get("/live/pedana").then((r) => { if (r.data?.current?.id) setCurrentId(r.data.current.id); }).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = eventId !== "all" ? { event_id: eventId } : {};
    api.get("/groups", { params }).then((r) => setGroups(r.data)).catch(() => setGroups([]));
    api.get("/athletes", { params }).then((r) => setAthletes(r.data)).finally(() => setLoading(false));
    setActiveIdx(-1);
    setPlaying(false);
  }, [eventId]);

  // tracce riproducibili nella barra interna: file caricati + link audio diretti (atleti + gruppi)
  const barTracks = [
    ...athletes
      .filter((a) => a.music_file_id || (a.music_url && isDirectAudio(a.music_url)))
      .map((a) => ({ id: a.id, music_title: a.music_title, subtitle: `${a.first_name} ${a.last_name}`, src: a.music_file_id ? `${API}/music/${a.music_file_id}` : a.music_url })),
    ...groups
      .filter((g) => g.music_file_id || (g.music_url && isDirectAudio(g.music_url)))
      .map((g) => ({ id: g.id, music_title: g.music_title, subtitle: g.name, src: g.music_file_id ? `${API}/music/${g.music_file_id}` : g.music_url })),
  ];
  const activeTrack = activeIdx >= 0 ? barTracks[activeIdx] : null;

  const playTrack = useCallback((idx) => {
    if (idx === activeIdx) {
      if (audioRef.current?.paused) { audioRef.current.play(); setPlaying(true); }
      else { audioRef.current?.pause(); setPlaying(false); }
      return;
    }
    setActiveIdx(idx);
    setPlaying(true);
  }, [activeIdx]);

  useEffect(() => {
    if (!activeTrack || !audioRef.current) return;
    audioRef.current.src = activeTrack.src;
    audioRef.current.load();
    const p = audioRef.current.play();
    if (p) p.then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [activeIdx]); // eslint-disable-line

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const next = () => { if (barTracks.length) setActiveIdx((i) => (i + 1) % barTracks.length); };
  const prev = () => { if (barTracks.length) setActiveIdx((i) => (i - 1 + barTracks.length) % barTracks.length); };
  const togglePlay = () => {
    if (activeIdx < 0 && barTracks.length) { setActiveIdx(0); setPlaying(true); return; }
    if (audioRef.current?.paused) { audioRef.current.play(); setPlaying(true); }
    else { audioRef.current?.pause(); setPlaying(false); }
  };
  const seek = (val) => { if (audioRef.current) { audioRef.current.currentTime = val[0]; setCurrent(val[0]); } };

  const canReorder = ["admin", "segreteria"].includes(user?.role);
  const moveAt = (arr, i, j) => { if (j < 0 || j >= arr.length) return null; const c = [...arr]; const [m] = c.splice(i, 1); c.splice(j, 0, m); return c; };
  const persistAthletes = (list) => { const prev = athletes; setAthletes(list); api.put("/athletes/reorder", { ids: list.map((a) => a.id) }).catch(() => { setAthletes(prev); toast.error("Errore nel salvataggio dell'ordine"); }); };
  const persistGroups = (type, typeList) => { const prev = groups; setGroups((p) => [...p.filter((g) => g.group_type !== type), ...typeList]); api.put("/groups/reorder", { ids: typeList.map((g) => g.id) }).catch(() => { setGroups(prev); toast.error("Errore nel salvataggio dell'ordine"); }); };
  const moveAthlete = (i, dir) => { const n = moveAt(athletes, i, i + dir); if (n) persistAthletes(n); };
  const moveGroup = (type, list, i, dir) => { const n = moveAt(list, i, i + dir); if (n) persistGroups(type, n); };
  const handleDrop = (type, toIndex) => {
    if (!dragCtx || dragCtx.type !== type) { setDragCtx(null); return; }
    if (type === "individuale") { const n = moveAt(athletes, dragCtx.i, toIndex); if (n) persistAthletes(n); }
    else { const list = groups.filter((g) => g.group_type === type); const n = moveAt(list, dragCtx.i, toIndex); if (n) persistGroups(type, n); }
    setDragCtx(null);
  };
  const arrows = (idKey, i, len, onUp, onDown) => (
    <div className="flex flex-col shrink-0">
      <button data-testid={`reorder-up-${idKey}`} onClick={onUp} disabled={i === 0} className="text-muted-foreground hover:text-gold disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
      <button data-testid={`reorder-down-${idKey}`} onClick={onDown} disabled={i === len - 1} className="text-muted-foreground hover:text-gold disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
    </div>
  );

  const flat = [
    ...athletes.map((a) => ({ id: a.id, label: `${a.first_name} ${a.last_name}`, sub: `${a.category} · Individuale` })),
    ...GROUP_TYPES.flatMap((t) => groups.filter((g) => g.group_type === t).map((g) => ({ id: g.id, label: g.name, sub: `${g.category} · ${TYPE_LABEL[t]}` }))),
  ];
  const curIdx = flat.findIndex((x) => x.id === currentId);
  const cur = curIdx >= 0 ? flat[curIdx] : null;
  const nxt = curIdx >= 0 ? (flat[curIdx + 1] || null) : (flat[0] || null);
  const nextId = nxt?.id;
  const advance = (dir) => { if (!flat.length) return; const ni = curIdx < 0 ? 0 : Math.min(flat.length - 1, Math.max(0, curIdx + dir)); markPedana(flat[ni].id); };
  const canControl = ["admin", "segreteria", "direttore"].includes(user?.role);
  const markPedana = (id) => {
    setCurrentId(id);
    if (canControl) api.put("/live/pedana", { current_id: id, event_id: eventId }).catch(() => {});
  };

  const eventTitle = eventId === "all" ? "Tutti gli eventi" : (events.find((e) => e.id === eventId)?.title || "Programma");

  const exportOrderPDF = async () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const now = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    const selectedEvent = eventId === "all" ? null : (events.find((e) => e.id === eventId) || null);
    let textX = 14;
    if (logoUrl) {
      try {
        const logo = await urlToImage(logoUrl);
        const h = 18, w = Math.min(40, (logo.width / logo.height) * h);
        doc.addImage(logo.dataUrl, logo.fmt, 14, 10, w, h);
        textX = 14 + w + 5;
      } catch (e) { /* logo opzionale */ }
    }
    doc.setFontSize(18); doc.setTextColor(20, 20, 20);
    doc.text("OPES Ginnastiche", textX, 17);
    let ly = 24;
    doc.setFontSize(13); doc.setTextColor(30, 30, 30);
    doc.text(eventTitle, textX, ly); ly += 6;
    doc.setFontSize(10); doc.setTextColor(90, 90, 90);
    const meta = ["Ordine di entrata in pedana"];
    if (selectedEvent?.location) meta.push(selectedEvent.location);
    if (selectedEvent?.date) meta.push(new Date(selectedEvent.date).toLocaleDateString("it-IT"));
    doc.text(meta.join("  •  "), textX, ly); ly += 5;
    doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`Generato il ${now}`, textX, ly);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5);
    let y = Math.max(32, ly + 4);
    doc.line(14, y, 196, y); y += 6;
    const section = (title, rows) => {
      if (!rows.length) return;
      doc.setFontSize(12); doc.setTextColor(212, 175, 55); doc.text(title, 14, y); y += 2;
      autoTable(doc, { startY: y + 2, head: [["#", "Nome", "Categoria / Società", "Disciplina"]], body: rows,
        theme: "grid", headStyles: { fillColor: [212, 175, 55], textColor: [2, 6, 23], fontStyle: "bold" },
        styles: { fontSize: 9, cellPadding: 1.5 }, margin: { left: 14, right: 14 } });
      y = doc.lastAutoTable.finalY + 8;
    };
    section("Individuale", athletes.map((a, i) => [i + 1, `${a.first_name} ${a.last_name}`, `${a.category}${a.club ? " · " + a.club : ""}`, discLabel[a.discipline]]));
    GROUP_TYPES.forEach((t) => {
      const list = groups.filter((g) => g.group_type === t);
      section(TYPE_LABEL[t], list.map((g, i) => [i + 1, g.name, `${g.category}${g.members?.length ? " · " + g.members.map((m) => m.name).join(", ") : ""}`, discLabel[g.discipline]]));
    });
    doc.save(`ordine-pedana-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportCsv = () => {
    const selectedEvent = eventId === "all" ? null : (events.find((e) => e.id === eventId) || null);
    const meta = [
      ["OPES Ginnastiche — Programma di Gara"],
      [eventTitle],
    ];
    if (selectedEvent?.location) meta.push([selectedEvent.location]);
    if (selectedEvent?.date) meta.push([new Date(selectedEvent.date).toLocaleDateString("it-IT")]);
    meta.push([`Generato il ${new Date().toLocaleDateString("it-IT")}`]);
    meta.push([]);
    const header = ["Ordine", "Nome", "Cognome", "Società", "Disciplina", "Categoria", "Musica", "Evento"];
    const rows = athletes.map((a, i) => [
      a.performance_order || i + 1, a.first_name, a.last_name, a.club || "",
      discLabel[a.discipline] || a.discipline, a.category, a.music_title || a.music_url || "", a.event_title || "",
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [...meta, header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `programma-gara-${eventTitle.replace(/\s+/g, "-").toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const printProgram = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const doc = w.document;
    doc.title = "Programma di Gara";
    const style = doc.createElement("style");
    style.textContent = "body{font-family:Georgia,serif;color:#111;padding:40px}.hd{display:flex;align-items:center;gap:16px;margin:0 0 6px}.hd img{height:56px;object-fit:contain}h1{font-size:26px;margin:0}h2{font-size:15px;color:#666;font-weight:400;margin:0 0 24px}table{width:100%;border-collapse:collapse;font-size:13px}th,td{border:1px solid #ccc;padding:8px 10px;text-align:left}th{background:#f3f3f3;text-transform:uppercase;font-size:11px;letter-spacing:.08em}";
    doc.head.appendChild(style);

    const selectedEvent = eventId === "all" ? null : (events.find((e) => e.id === eventId) || null);
    const hd = doc.createElement("div");
    hd.className = "hd";
    if (logoUrl) {
      const img = doc.createElement("img");
      img.src = logoUrl; img.alt = "OPES";
      hd.appendChild(img);
    }
    const h1 = doc.createElement("h1");
    h1.textContent = "OPES Ginnastiche — Programma di Gara";
    hd.appendChild(h1);
    const h2 = doc.createElement("h2");
    const meta2 = [eventTitle];
    if (selectedEvent?.location) meta2.push(selectedEvent.location);
    if (selectedEvent?.date) meta2.push(new Date(selectedEvent.date).toLocaleDateString("it-IT"));
    meta2.push(`${athletes.length} partecipanti`);
    h2.textContent = meta2.join(" · ");
    doc.body.appendChild(hd);
    doc.body.appendChild(h2);

    const table = doc.createElement("table");
    const thead = doc.createElement("thead");
    const htr = doc.createElement("tr");
    ["Ordine", "Atleta", "Società", "Disciplina", "Categoria", "Musica"].forEach((h) => {
      const th = doc.createElement("th"); th.textContent = h; htr.appendChild(th);
    });
    thead.appendChild(htr);
    table.appendChild(thead);

    const tbody = doc.createElement("tbody");
    athletes.forEach((a, i) => {
      const tr = doc.createElement("tr");
      const cells = [
        String(a.performance_order || i + 1),
        `${a.first_name} ${a.last_name}`,
        a.club || "—",
        discLabel[a.discipline] || a.discipline,
        a.category,
        a.music_title || (a.music_url ? "Link esterno" : "—"),
      ];
      cells.forEach((c, idx) => {
        const td = doc.createElement("td");
        td.textContent = c;
        if (idx === 0) { td.style.textAlign = "center"; td.style.fontWeight = "600"; }
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    doc.body.appendChild(table);

    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <div className="pb-28">
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={next}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <p className="text-muted-foreground">Ordine di esibizione con nomi e tracce musicali.</p>
        <div className="flex items-center gap-3">
          {canExport && (
            <>
              <Button data-testid="export-program-btn" onClick={exportCsv} variant="outline" className="border-white/15 hover:border-gold/60" disabled={athletes.length === 0}>
                <Download className="w-4 h-4 mr-1.5" /> Esporta CSV
              </Button>
              <Button data-testid="print-program-btn" onClick={printProgram} variant="outline" className="border-white/15 hover:border-gold/60" disabled={athletes.length === 0}>
                <Printer className="w-4 h-4 mr-1.5" /> Stampa
              </Button>
              <Button data-testid="pdf-order-btn" onClick={exportOrderPDF} variant="outline" className="border-white/15 hover:border-gold/60" disabled={athletes.length === 0 && groups.length === 0}>
                <Download className="w-4 h-4 mr-1.5" /> PDF Ordine
              </Button>
            </>
          )}
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger data-testid="program-event-filter" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli eventi</SelectItem>
              {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}
            </SelectContent>
          </Select>
          {levelOptions.length > 0 && (
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger data-testid="program-level-filter" className="w-44"><SelectValue placeholder="Tutti i livelli" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i livelli</SelectItem>
                {levelOptions.map((lv) => <SelectItem key={lv} value={lv}>{`Livello ${lv}`}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : (athletes.length === 0 && groups.length === 0) ? (
        <div className="text-center py-20 text-muted-foreground border border-dashed border-white/10 rounded-xl">Nessun partecipante nel programma.</div>
      ) : (
        <div className="space-y-10">
          <div data-testid="pedana-banner" className="rounded-xl border border-gold/30 bg-gold/5 p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[140px]">
              <p className="text-xs uppercase tracking-wide text-gold">In pedana ora</p>
              <p data-testid="pedana-current" className="font-display text-xl truncate">{cur ? cur.label : "—"}{cur && <span className="text-sm text-muted-foreground"> · {cur.sub}</span>}</p>
            </div>
            <div className="flex-1 min-w-[140px]">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Il prossimo</p>
              <p data-testid="pedana-next" className="text-base truncate">{nxt ? nxt.label : "—"}{nxt && <span className="text-sm text-muted-foreground"> · {nxt.sub}</span>}</p>
            </div>
            {canReorder && (
              <div className="flex items-center gap-2">
                <Button data-testid="pedana-prev-btn" onClick={() => advance(-1)} variant="outline" size="sm" className="border-white/15"><SkipBack className="w-4 h-4" /></Button>
                <Button data-testid="pedana-next-btn" onClick={() => advance(1)} size="sm" className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">Avanti <SkipForward className="w-4 h-4 ml-1.5" /></Button>
              </div>
            )}
          </div>
          {athletes.length > 0 && (
            <section data-testid="program-type-individuale">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-gold" />
                <h3 className="font-display text-xl">Individuale</h3>
                <span className="text-xs text-muted-foreground">{athletes.length} in pedana</span>
              </div>
              <div className="space-y-3">
                {athletes.map((a, i) => {
                  const barIdx = barTracks.findIndex((t) => t.id === a.id);
                  const hasBar = barIdx >= 0;
                  const isActive = hasBar && barIdx === activeIdx;
                  const isPlatform = !hasBar && a.music_url;
                  const hiddenRow = levelFilter !== "all" && a.level !== levelFilter;
                  return (
                    <div key={a.id} data-testid={`program-row-${a.id}`}
                      draggable={canReorder && levelFilter === "all"} onDragStart={() => setDragCtx({ type: "individuale", i })} onDragOver={(e) => e.preventDefault()} onDrop={() => handleDrop("individuale", i)}
                      className={`${hiddenRow ? "hidden" : "flex"} items-center gap-3 rounded-xl border p-4 transition-colors ${a.id === currentId ? "border-gold ring-2 ring-gold/50 bg-gold/10" : isActive ? "border-gold/60 bg-gold/5" : a.id === nextId ? "border-gold/40 border-dashed" : "border-white/10 bg-[#0F172A] hover:border-gold/30"}`}>
                      {canReorder && levelFilter === "all" && <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" />}
                      <button data-testid={`program-order-${a.id}`} onClick={() => markPedana(a.id)} title="Segna come in pedana" className="w-10 h-10 shrink-0 rounded-lg bg-gold/10 text-gold font-display text-xl flex items-center justify-center hover:bg-gold hover:text-[#020617] transition-colors">{i + 1}</button>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{a.first_name} {a.last_name}</p>
                        <p className="text-sm text-muted-foreground truncate">{a.club || "—"} · {a.category}{a.level ? ` · Liv. ${a.level}` : ""}</p>
                      </div>
                      <Badge variant="outline" className="border-gold/40 text-gold hidden sm:inline-flex">{discLabel[a.discipline]}</Badge>
                      <div className="flex items-center gap-2 min-w-0 max-w-[180px]">
                        <Music4 className="w-4 h-4 text-gold shrink-0" />
                        <span className="text-sm text-muted-foreground truncate">{a.music_title || (a.music_url ? "Link esterno" : "Nessuna musica")}</span>
                      </div>
                      {hasBar ? (
                        <button data-testid={`play-music-${a.music_file_id || a.id}`} onClick={() => playTrack(barIdx)}
                          className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-colors ${isActive && playing ? "bg-gold text-[#020617]" : "bg-gold/10 text-gold hover:bg-gold hover:text-[#020617]"}`}>
                          {isActive && playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                        </button>
                      ) : isPlatform ? (
                        <button data-testid={`play-url-${a.id}`} onClick={() => setExtTrack({ title: a.music_title || "Traccia esterna", subtitle: `${a.first_name} ${a.last_name}`, url: a.music_url })}
                          className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-gold/10 text-gold hover:bg-gold hover:text-[#020617] transition-colors" title="Riproduci da piattaforma esterna">
                          <Play className="w-4 h-4 ml-0.5" />
                        </button>
                      ) : (
                        <span className="w-10 shrink-0" />
                      )}
                      {canReorder && arrows(a.id, i, athletes.length, () => moveAthlete(i, -1), () => moveAthlete(i, 1))}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {GROUP_TYPES.map((type) => {
            const list = groups.filter((g) => g.group_type === type);
            if (list.length === 0) return null;
            return (
              <section key={type} data-testid={`program-type-${type}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Users2 className="w-5 h-5 text-gold" />
                  <h3 className="font-display text-xl">{TYPE_LABEL[type]}</h3>
                  <span className="text-xs text-muted-foreground">{list.length} in pedana</span>
                </div>
                <div className="space-y-3">
                  {list.map((g, i) => {
                    const barIdx = barTracks.findIndex((t) => t.id === g.id);
                    const hasBar = barIdx >= 0;
                    const isActive = hasBar && barIdx === activeIdx;
                    const isPlatform = !hasBar && g.music_url;
                    return (
                    <div key={g.id} data-testid={`program-group-row-${g.id}`}
                      draggable={canReorder} onDragStart={() => setDragCtx({ type, i })} onDragOver={(e) => e.preventDefault()} onDrop={() => handleDrop(type, i)}
                      className={`flex items-center gap-3 rounded-xl border p-4 transition-colors ${g.id === currentId ? "border-gold ring-2 ring-gold/50 bg-gold/10" : isActive ? "border-gold/60 bg-gold/5" : g.id === nextId ? "border-gold/40 border-dashed" : "border-white/10 bg-[#0F172A] hover:border-gold/30"}`}>
                      {canReorder && <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" />}
                      <button data-testid={`program-order-${g.id}`} onClick={() => markPedana(g.id)} title="Segna come in pedana" className="w-10 h-10 shrink-0 rounded-lg bg-gold/10 text-gold font-display text-xl flex items-center justify-center hover:bg-gold hover:text-[#020617] transition-colors">{i + 1}</button>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{g.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{g.members?.map((m) => m.name).join(", ") || "Nessuna atleta"} · {g.category}</p>
                      </div>
                      <Badge variant="outline" className="border-gold/40 text-gold hidden sm:inline-flex">{discLabel[g.discipline]}</Badge>
                      <div className="flex items-center gap-2 min-w-0 max-w-[180px]">
                        <Music4 className="w-4 h-4 text-gold shrink-0" />
                        <span className="text-sm text-muted-foreground truncate">{g.music_title || (g.music_url ? "Link esterno" : "Nessuna musica")}</span>
                      </div>
                      {hasBar ? (
                        <button data-testid={`play-group-music-${g.id}`} onClick={() => playTrack(barIdx)}
                          className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-colors ${isActive && playing ? "bg-gold text-[#020617]" : "bg-gold/10 text-gold hover:bg-gold hover:text-[#020617]"}`}>
                          {isActive && playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                        </button>
                      ) : isPlatform ? (
                        <button data-testid={`play-group-url-${g.id}`} onClick={() => setExtTrack({ title: g.music_title || "Traccia esterna", subtitle: g.name, url: g.music_url })}
                          className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-gold/10 text-gold hover:bg-gold hover:text-[#020617] transition-colors" title="Riproduci da piattaforma esterna">
                          <Play className="w-4 h-4 ml-0.5" />
                        </button>
                      ) : (
                        <span className="w-10 shrink-0" />
                      )}
                      {canReorder && arrows(g.id, i, list.length, () => moveGroup(type, list, i, -1), () => moveGroup(type, list, i, 1))}
                    </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Player bar */}
      {activeTrack && (
        <motion.div data-testid="music-player-bar" initial={{ y: 100 }} animate={{ y: 0 }} transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="fixed bottom-0 left-0 lg:left-72 right-0 z-30 bg-black/70 backdrop-blur-xl border-t border-white/10 px-4 sm:px-6 py-3">
          <div className="max-w-6xl mx-auto flex items-center gap-4">
            <div className="w-11 h-11 shrink-0 rounded-lg bg-gold/15 text-gold flex items-center justify-center">
              <Music4 className="w-5 h-5" />
            </div>
            <div className="min-w-0 w-40 sm:w-52">
              <p className="font-medium truncate text-sm" data-testid="player-track-title">{activeTrack.music_title || "Traccia"}</p>
              <p className="text-xs text-muted-foreground truncate">{activeTrack.subtitle}</p>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="player-prev-btn" onClick={prev} className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"><SkipBack className="w-4 h-4" /></button>
              <button data-testid="player-play-btn" onClick={togglePlay} className="w-11 h-11 rounded-full bg-gold text-[#020617] flex items-center justify-center hover:bg-[#FDE047] transition-colors">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <button data-testid="player-next-btn" onClick={next} className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"><SkipForward className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 flex items-center gap-3 min-w-0">
              <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">{fmt(current)}</span>
              <Slider data-testid="player-seek" value={[current]} max={duration || 0} step={0.1} onValueChange={seek} className="flex-1" />
              <span className="text-xs text-muted-foreground tabular-nums w-10">{fmt(duration)}</span>
            </div>
            <div className="hidden md:flex items-center gap-2 w-32">
              <button data-testid="player-mute-btn" onClick={() => setMuted((m) => !m)} className="text-muted-foreground hover:text-foreground transition-colors">
                {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <Slider value={[muted ? 0 : volume]} max={1} step={0.05} onValueChange={(v) => { setVolume(v[0]); setMuted(false); }} className="flex-1" />
            </div>
          </div>
        </motion.div>
      )}

      {/* External platform player dialog */}
      <Dialog open={!!extTrack} onOpenChange={(v) => { if (!v) setExtTrack(null); }}>
        <DialogContent className="bg-[#0F172A] border-white/10 max-w-2xl" data-testid="external-player-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Music4 className="w-5 h-5 text-gold" /> {extTrack?.title}
            </DialogTitle>
          </DialogHeader>
          {extTrack && (() => {
            const embed = getEmbed(extTrack.url);
            return (
              <div>
                <p className="text-sm text-muted-foreground -mt-2 mb-3">{extTrack.subtitle}</p>
                {embed ? (
                  <div className="rounded-lg overflow-hidden border border-white/10">
                    <iframe
                      title="Lettore esterno"
                      data-testid="external-player-iframe"
                      src={embed.src}
                      style={{ height: embed.height }}
                      className="w-full"
                      allow="autoplay; encrypted-media; clipboard-write; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-white/15 p-6 text-center">
                    <p className="text-sm text-muted-foreground">Anteprima non disponibile per questa piattaforma. Puoi comunque aprire il brano nella piattaforma originale.</p>
                  </div>
                )}
                <a
                  data-testid="external-player-open-link"
                  href={extTrack.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm text-gold hover:text-[#FDE047] transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Apri nella piattaforma {embed ? `(${embed.type})` : ""}
                </a>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
